import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGetGames } from '../../../hooks/queries/games/games/use-get-games'
import type { Game } from '../../../services/games/games/types'

// Webview de Jogo Responsavel (termo + limite diario + extrato do dia)
const CONSENT_GAME_URL =
  window.location.hostname === 'localhost' ||
  window.location.hostname.includes('-dev')
    ? 'https://consent-game-dev.didderlive.com'
    : 'https://consent-game.didderlive.com'

function postMessage(message: string) {
  if ('ReactNativeWebView' in window) {
    // @ts-ignore
    window.ReactNativeWebView.postMessage(message)
  }
}

// Jogo Responsavel "uma vez ao dia": guarda a data (local) do ultimo consent
// concluido. Enquanto for hoje, entra nos jogos direto; vira o dia, mostra de novo.
const CONSENT_DATE_KEY = '@consent-game-date'

function localDateStr(): string {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

function consentSeenToday(): boolean {
  try {
    return localStorage.getItem(CONSENT_DATE_KEY) === localDateStr()
  } catch {
    return false
  }
}

function markConsentSeenToday(): void {
  try {
    localStorage.setItem(CONSENT_DATE_KEY, localDateStr())
  } catch {
    // localStorage indisponivel: no pior caso o consent aparece de novo (seguro)
  }
}

export const ProfileGames = memo(() => {
  const [selectedGame, setSelectedGame] = useState<Game | null>(null)
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false)
  // Data safety (Jogo Responsavel): overlay de consentimento/status
  const [consentOpen, setConsentOpen] = useState(false)
  const [pendingGame, setPendingGame] = useState<Game | null>(null)
  const consentOpenRef = useRef(false)
  const pendingGameRef = useRef<Game | null>(null)
  const consentCompletedRef = useRef(false)

  const openConsent = useCallback((game: Game | null) => {
    pendingGameRef.current = game
    setPendingGame(game)
    consentCompletedRef.current = false
    consentOpenRef.current = true
    setConsentOpen(true)
  }, [])
  const searchParams = useMemo(() => {
    return new URLSearchParams(window.location.search)
  }, [])

  const token = useMemo(() => {
    return searchParams.get('token') ?? localStorage.getItem('@token')
  }, [searchParams])

  const hideGameBackButton = useMemo(() => {
    return searchParams.get('hideGameBackButton') === '1'
  }, [searchParams])

  const mainContainerHeight = useMemo(() => {
    // jogo sempre em tela cheia; esconder o botão voltar não deve encolher pra 50vh
    return '100vh'
  }, [hideGameBackButton])

  const { games, isLoading } = useGetGames(
    {
      active: true,
      deleted: false,
      take: 100,
      skip: 0,
    },
    { enabled: Boolean(token), token },
  )

  const selectedGameUrl = useMemo(() => {
    if (!selectedGame?.url || !token) {
      return null
    }

    const separator = selectedGame.url.includes('?') ? '&' : '?'
    const hideBackButtonParam = hideGameBackButton ? '&hideBackButton=1' : ''
    const slotGameIdParam =
      selectedGame.slotGameId != null ? `&slotGameId=${selectedGame.slotGameId}` : ''

    return `${selectedGame.url}${separator}token=${token}${hideBackButtonParam}${slotGameIdParam}`
  }, [hideGameBackButton, selectedGame, token])

  const handleSelectGame = useCallback(
    (game: Game) => {
      // Jogo Responsavel UMA VEZ AO DIA: se ja viu hoje, entra direto; senao,
      // mostra o consent (e a data e registrada ao concluir o "Continuar").
      // A trava real permanece: o backend do jogo manda 'open-consent-game' se o
      // usuario estiver bloqueado (limite/perda), e o botao 🛡️ Limites abre sob demanda.
      if (consentSeenToday()) {
        setSelectedGame(game)
      } else {
        openConsent(game)
      }
    },
    [openConsent],
  )

  const handleCloseGame = useCallback(() => {
    setIsCloseConfirmOpen(false)
    setSelectedGame(null)
  }, [])

  const handleCancelClose = useCallback(() => {
    setIsCloseConfirmOpen(false)
  }, [])

  const handleBack = useCallback(() => {
    if (!selectedGame) {
      postMessage('close')
      // fallback: se o postMessage nao chegar no RN (bridge indisponivel),
      // o deep-link e interceptado pelo onShouldStartLoadWithRequest da tela
      // nativa e fecha a webview. Se o RN ja fechou, o timeout nem roda.
      window.setTimeout(() => {
        window.location.href = 'zury://close-games'
      }, 300)
      return
    }

    setIsCloseConfirmOpen(true)
  }, [selectedGame])

  useEffect(() => {
    const handleNativeBack = (event: MessageEvent<string>) => {
      // overlay de consentimento aberto: as mensagens sao DELE (prioridade)
      if (consentOpenRef.current) {
        if (event.data === 'consent-completed') {
          consentCompletedRef.current = true
          return
        }
        if (
          event.data === 'native-back' ||
          event.data === 'close' ||
          event.data === 'close-game' ||
          event.data === 'close-game-now'
        ) {
          consentOpenRef.current = false
          setConsentOpen(false)
          const nextGame = pendingGameRef.current
          pendingGameRef.current = null
          setPendingGame(null)
          // so entra no jogo se concluiu a tela de status ("Continuar")
          if (nextGame && consentCompletedRef.current) {
            // concluiu o consent pre-jogo → nao mostra de novo ate virar o dia
            markConsentSeenToday()
            setSelectedGame(nextGame)
          }
          consentCompletedRef.current = false
          return
        }
        return
      }

      // jogo bloqueado pelo Jogo Responsavel: abre o consentimento por cima
      if (event.data === 'open-consent-game') {
        openConsent(null)
        return
      }

      // 'native-back' vem do RN; 'close-game' vem do icone de sair dentro do jogo (iframe)
      if (event.data === 'native-back' || event.data === 'close-game') {
        handleBack()
      }
      // 'close-game-now': fecha o webview DIRETO, sem o modal de confirmação
      // (usado por jogos cujo botão de voltar deve sair na hora, ex.: loteria)
      if (event.data === 'close-game-now') {
        setSelectedGame(null)
        postMessage('close')
      }
    }

    window.addEventListener('message', handleNativeBack)
    document.addEventListener('message', handleNativeBack as EventListener)

    return () => {
      window.removeEventListener('message', handleNativeBack)
      document.removeEventListener('message', handleNativeBack as EventListener)
    }
  }, [handleBack, openConsent])

  // só jogos ativos aparecem na tela
  const activeGames = useMemo(() => {
    return games?.filter((game) => game.active) ?? []
  }, [games])

  const slotGames = useMemo(() => {
    return activeGames.filter(
      (game) => game.gameSection?.trim().toLowerCase() === 'slots',
    )
  }, [activeGames])

  const spinWheelGames = useMemo(() => {
    return activeGames.filter((game) => {
      const section = game.gameSection?.trim().toLowerCase()

      return section === 'spin-wheel' || section === 'spin wheel'
    })
  }, [activeGames])

  const otherGames = useMemo(() => {
    return activeGames.filter((game) => {
      const section = game.gameSection?.trim().toLowerCase()

      return (
        section !== 'slots' &&
        section !== 'spin-wheel' &&
        section !== 'spin wheel'
      )
    })
  }, [activeGames])

  return (
    <>
      {consentOpen && token && (
        <div className="fixed inset-0 z-40 bg-[#f2f3f5]">
          <iframe
            src={`${CONSENT_GAME_URL}/?token=${token}${pendingGame ? '&mode=pre-game' : ''}`}
            title="jogo-responsavel"
            className="h-full w-full border-0"
          />
        </div>
      )}

      {selectedGameUrl && (
        <div className="pointer-events-none fixed inset-0 z-30 flex items-end justify-center bg-transparent">
          <iframe
            src={selectedGameUrl}
            title={selectedGame?.gameKey ?? 'profile-game'}
            className="pointer-events-auto w-full border-0 bg-transparent"
            allow="fullscreen"
            allowTransparency={true}
            style={{
              backgroundColor: 'transparent',
              height: selectedGame?.height || mainContainerHeight,
            }}
          />

          {isCloseConfirmOpen && (
            <div
              className="pointer-events-auto absolute inset-0 z-20 flex items-center justify-center bg-black/70 px-6 backdrop-blur-sm"
              onClick={handleCancelClose}
            >
              <div
                role="dialog"
                aria-modal="true"
                onClick={(event) => event.stopPropagation()}
                className="w-full max-w-sm rounded-3xl border border-[rgba(45,32,24,0.08)] bg-white p-6 text-center shadow-2xl"
              >
                <h2 className="text-lg font-black text-[#2D2018]">
                  Fechar o jogo?
                </h2>

                <p className="mt-2 text-sm text-[#8F6C58]">
                  Seu progresso atual da rodada pode ser perdido.
                </p>

                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancelClose}
                    className="flex-1 rounded-full border border-[rgba(45,32,24,0.15)] bg-white px-4 py-3 text-sm font-bold text-[#2D2018]"
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    onClick={handleCloseGame}
                    className="flex-1 rounded-full bg-[#E67E43] px-4 py-3 text-sm font-bold text-white"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div
        className="w-screen bg-[#FFF8F2] text-[#2D2018]"
        style={{ minHeight: mainContainerHeight }}
      >
      <div
        className="mx-auto flex w-full max-w-5xl flex-col px-4 py-6"
        style={{ minHeight: mainContainerHeight }}
      >
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              aria-label="Voltar"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(45,32,24,0.12)] bg-white text-xl font-bold text-[#2D2018] shadow-sm"
            >
              ‹
            </button>

            <h1 className="text-2xl font-black tracking-tight text-[#2D2018]">
              Games
            </h1>
          </div>

          <button
            type="button"
            onClick={() => openConsent(null)}
            aria-label="Jogo responsável"
            className="rounded-full border border-[#E67E43]/30 bg-[#E67E43]/10 px-4 py-2 text-sm font-bold text-[#B85C24]"
          >
            🛡️ Limites
          </button>
        </div>

        <section className="mb-8">
          <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.24em] text-[#8F6C58]">
            Slots
          </h2>

          {isLoading && (
            <span className="text-sm text-[#8F6C58]">Carregando...</span>
          )}

          <div className="flex flex-wrap gap-4">
            {slotGames.map((game) => (
              <button
                key={game.id}
                type="button"
                onClick={() => handleSelectGame(game)}
                className="aspect-square flex-[1_1_calc(25%-0.75rem)] max-w-[calc(25%-0.75rem)] overflow-hidden rounded-2xl border border-[rgba(45,32,24,0.08)] bg-white shadow-sm"
              >
                <img
                  src={game.imgLogo}
                  alt={game.gameLabel}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.24em] text-[#8F6C58]">
            Spin Wheel
          </h2>

          <div className="flex flex-wrap gap-4">
            {spinWheelGames.map((game) => (
              <button
                key={game.id}
                type="button"
                onClick={() => handleSelectGame(game)}
                className="aspect-square flex-[1_1_calc(25%-0.75rem)] max-w-[calc(25%-0.75rem)] overflow-hidden rounded-2xl border border-[rgba(45,32,24,0.08)] bg-white shadow-sm"
              >
                <img
                  src={game.imgLogo}
                  alt={game.gameLabel}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.24em] text-[#8F6C58]">
            Others
          </h2>

          <div className="flex flex-wrap gap-4">
            {otherGames.map((game) => (
              <button
                key={game.id}
                type="button"
                onClick={() => handleSelectGame(game)}
                className="aspect-square flex-[1_1_calc(25%-0.75rem)] max-w-[calc(25%-0.75rem)] overflow-hidden rounded-2xl border border-[rgba(45,32,24,0.08)] bg-white shadow-sm"
              >
                <img
                  src={game.imgLogo}
                  alt={game.gameLabel}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </section>
      </div>
      </div>
    </>
  )
})

ProfileGames.displayName = 'ProfileGames'
